import { index, prop } from "@typegoose/typegoose";
import { mongoose } from "@typegoose/typegoose";
import { z } from "zod";
import { ObjectIdString, validate } from "../../zod-decorator/index.js";
import { SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";

/**
 * Abstract base class for SCD Type 2 versioned documents.
 *
 * Every versioned document carries the following temporal bookkeeping fields:
 * - `recordId`          — shared ObjectId across all versions of one logical entity
 * - `versionId`         — monotonically increasing integer (1, 2, 3, …); unique per record
 * - `versionValidFrom`  — the transaction timestamp when this version became active
 * - `versionValidTo`    — the transaction timestamp when this version was superseded;
 *                         absent on the current (most-recent) version
 * - `deleted`           — `true` when this version represents a logical deletion;
 *                         always a boolean, never absent
 * - `versionIsCurrent`  — `true` on the most-recent (unsuperseded) version of the record,
 *                         whether active (`deleted: false`) or logically deleted (`deleted: true`);
 *                         `false` on all historical (superseded) versions;
 *                         always a boolean, never absent
 * - `versionEventId`    — reference to the `VersionEvent` audit sidecar
 * - `versionCreatedAt`  — timestamp when THIS version document was inserted
 * - `recordCreatedAt`   — timestamp when version 1 was created (carried forward on every
 *                         subsequent version so the record's original creation time is
 *                         always available on the current document without a history lookup)
 *
 * ## Querying
 *
 * | Goal | Query |
 * |---|---|
 * | Most-recent version (active or deleted) | `{ versionIsCurrent: true }` |
 * | Active non-deleted version | `{ versionIsCurrent: true, deleted: false }` |
 * | Full history, oldest first | `{ recordId }` sorted by `versionId` |
 *
 * ## Partial unique index
 *
 * Each versioned entity schema should define a partial unique index on its logical
 * identity fields (the fields that together identify one logical record, e.g.
 * `foreignLookup + warningType + warningSourceKey` for warnings):
 *
 * ```ts
 * @index(
 *     { fieldA: 1, fieldB: 1 },
 *     { unique: true, partialFilterExpression: { versionIsCurrent: true, deleted: false } }
 * )
 * ```
 *
 * This enforces at most one **active non-deleted** current version per logical identity,
 * while allowing a simultaneously existing deleted current version (e.g. after a logical
 * delete before a re-raise creates a new record with a fresh `recordId`).
 *
 * ## Timestamps
 *
 * **Do not use `timestamps: true` on SCD schemas.** Mongoose's `createdAt`/`updatedAt`
 * fields are ambiguous in a versioned context and are replaced by the purpose-specific
 * `versionCreatedAt` and `recordCreatedAt` fields.
 *
 * ## Version identity
 *
 * The compound unique index on `{ recordId, versionId }` prevents duplicate version
 * numbers within a single record's history.
 */
@index({ recordId: 1, versionId: 1 }, { unique: true })
@index({ versionValidFrom: 1 })
@index({ versionValidTo: 1 })
export abstract class ScdDocument<
    ImplementingSchema extends ScdDocument<any>,
    FieldsWithDefaults extends string = never,
> extends SchemaBase<ImplementingSchema, FieldsWithDefaults | "deleted"> {
    /**
     * Shared ObjectId across all versions of this logical entity.
     * All version documents with the same `recordId` represent the history of one record.
     */
    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: true })
    public recordId!: mongoose.Types.ObjectId;

    /**
     * Monotonically increasing version counter starting at 1.
     * The unique compound index on `{ recordId, versionId }` enforces that each
     * (record, version) pair exists at most once in the collection.
     */
    // Sent on every response; optional on create (the SCD layer assigns it), but REQUIRED on update so
    // the client's intended version can be checked against the current one (optimistic concurrency).
    @validate({ zodValidator: z.number().int().positive(), hasDefault: true, requiredOnUpdate: true })
    @prop({ type: () => Number, required: true })
    public versionId!: number;

    /**
     * Timestamp when this version became the active version.
     * Equal to `versionValidTo` of the immediately preceding version (if any).
     * Equal to `VersionEvent.timestamp` for this version transition.
     */
    @validate({ zodValidator: z.coerce.date(), responseOnly: true })
    @prop({ type: () => Date, required: true })
    public versionValidFrom!: Date;

    /**
     * Timestamp when this version was superseded by a newer version.
     * Absent on the current (most-recent) version.
     * Equal to `versionValidFrom` of the immediately following version.
     */
    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public versionValidTo?: Date;

    /**
     * Whether this version represents a logically deleted record.
     * Deletion is a version transition (`deleted: true`), not a physical removal.
     * Undeletion is another version transition (`deleted: false`).
     * Always a boolean — never absent.
     */
    @validate({ zodValidator: z.boolean(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public deleted: boolean = false;

    /**
     * Whether this is the most-recent (unsuperseded) version of this record.
     *
     * `true`  — this is the current version; may be active (`deleted: false`) or
     *            logically deleted (`deleted: true`).
     * `false` — this is a historical version that has been superseded by a newer one.
     *
     * Always a boolean — never absent. The SCD layer sets this to `true` on every
     * newly inserted version and to `false` when superseding the old current version.
     *
     * The recommended partial unique index on entity schemas uses
     * `partialFilterExpression: { versionIsCurrent: true, deleted: false }`, which enforces
     * at most one active non-deleted version per logical identity.
     */
    @validate({ zodValidator: z.boolean(), responseOnly: true })
    @prop({ type: () => Boolean, required: true })
    public versionIsCurrent!: boolean;

    /**
     * Reference to the VersionEvent sidecar that describes the audit metadata
     * (who changed what, when, and what kind of change it was) for this version transition.
     */
    @prop({ type: () => mongoose.Types.ObjectId, required: true })
    public versionEventId!: mongoose.Types.ObjectId;

    /**
     * Timestamp when THIS specific version document was inserted.
     *
     * Distinct from `versionValidFrom` (which is the shared transaction timestamp, also written
     * to `versionValidTo` of the superseded version and `VersionEvent.timestamp`) and from
     * `recordCreatedAt` (which tracks when the logical record was first created).
     *
     * Set by the SCD layer on every insert; never inherited from the previous version.
     */
    @prop({ type: () => Date, required: true })
    public versionCreatedAt!: Date;

    /**
     * Timestamp when version 1 of this logical record was first created.
     *
     * Identical to `versionValidFrom` on the first version. Carried forward unchanged on every
     * subsequent version transition so that "when was this record originally created?"
     * can be answered from the current version without a history lookup.
     */
    @prop({ type: () => Date, required: true })
    public recordCreatedAt!: Date;

    constructor(properties?: SchemaConstructorArgs<ImplementingSchema, FieldsWithDefaults | "deleted">) {
        super(properties);
        Object.assign(this, properties);
    }
}
